import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { paginatePreviewItems } from "../../shared/paginatePreviewItems";

const WIDTH = 794, HEIGHT = 1123, PADDING = 48;

export default function PaginatedQuestionPreview({ items, header, label = "A4 考卷預覽", fontFamily, fontSize = 16 }) {
  const viewport = useRef(null), measure = useRef(null);
  const [width, setWidth] = useState(WIDTH);
  const [actualSize, setActualSize] = useState(false);
  const [metrics, setMetrics] = useState({ heights: [], header: 0 });
  useLayoutEffect(() => {
    const element = viewport.current;
    const update = () => setWidth(element.clientWidth || WIDTH);
    update();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(element);
    window.addEventListener("resize", update);
    return () => { observer?.disconnect(); window.removeEventListener("resize", update); };
  }, []);
  useLayoutEffect(() => {
    const element = measure.current;
    let frame, disposed = false;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (disposed) return;
        const headerHeight = element.firstElementChild.getBoundingClientRect().height;
        const heights = Array.from(element.children).slice(1).map((child) => child.getBoundingClientRect().height);
        setMetrics((current) => current.header === headerHeight && JSON.stringify(current.heights) === JSON.stringify(heights)
          ? current : { header: headerHeight, heights });
      });
    };
    update();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    Array.from(element.children).forEach((child) => observer?.observe(child));
    element.addEventListener("load", update, true);
    document.fonts?.ready.then(() => { if (!disposed) update(); });
    return () => { disposed = true; cancelAnimationFrame(frame); observer?.disconnect(); element.removeEventListener("load", update, true); };
  }, [items, header, fontFamily, fontSize]);
  const capacity = Math.max(100, HEIGHT - PADDING * 2 - metrics.header);
  const pages = paginatePreviewItems(items, metrics.heights, capacity);
  const heightById = new Map(items.map((item, index) => [item.id, metrics.heights[index] || 1]));
  const oversized = items.some((item) => heightById.get(item.id) > capacity);
  const scale = actualSize ? 1 : Math.min(1, Math.max(0.2, (width - 32) / WIDTH));
  return <Root aria-label={label} style={{ fontFamily, fontSize }}>
    <Controls>
      <span>{items.length} 題 · {pages.length} 頁 · A4</span>
      <button type="button" aria-pressed={!actualSize} onClick={() => setActualSize(false)}>符合寬度</button>
      <button type="button" aria-pressed={actualSize} onClick={() => setActualSize(true)}>100%</button>
    </Controls>
    {oversized && <small role="status">有題目超過一頁，已完整展開；Word 的實際換頁請以匯出檔為準。</small>}
    <Viewport ref={viewport}>
      <Measurement ref={measure} aria-hidden="true" inert="" style={{ width: WIDTH - PADDING * 2 }}>
        <Block>{header}</Block>
        {items.map((item) => <Block key={item.id}>{item.content}</Block>)}
      </Measurement>
      {!items.length && <Empty>尚未選題，請先回到第一步加入題目。</Empty>}
      {pages.map((page, index) => {
        const height = Math.max(HEIGHT, PADDING * 2 + metrics.header + page.reduce((sum, item) => sum + heightById.get(item.id), 0));
        return <PageFrame key={page[0].id} style={{ width: WIDTH * scale, height: height * scale }}>
          <Paper aria-label={`第 ${index + 1} 頁`} style={{ width: WIDTH, minHeight: height, transform: `scale(${scale})` }}>
            <Block>{header}</Block>
            {page.map((item) => <Block key={item.id} data-preview-question-id={item.id}>{item.content}</Block>)}
            <PageNumber>{index + 1} / {pages.length}</PageNumber>
          </Paper>
        </PageFrame>;
      })}
    </Viewport>
    <small>依內容高度估算分頁；Word 的字型與換頁可能略有差異。</small>
  </Root>;
}
const Root = styled.div`min-width: 0; color: #171717; line-height: 1.5; h3 { text-align: center; }`;
const Controls = styled.div`display: flex; align-items: center; gap: 8px; padding: 10px 12px; font: 13px system-ui; color: var(--app-text, #333); background: var(--app-surface, white); border-radius: 8px; span { margin-right: auto; } button { padding: 6px 10px; border: 1px solid #c9cdd4; border-radius: 6px; cursor: pointer; } button[aria-pressed="true"] { background: #e4ecff; border-color: #5375bc; }`;
const Viewport = styled.div`position: relative; overflow: auto; max-height: calc(100vh - 230px); min-height: 420px; background: #e7e9ed; border-radius: 8px; padding: 16px; box-sizing: border-box;`;
const Measurement = styled.div`position: absolute; left: -10000px; top: 0; visibility: hidden; pointer-events: none;`;
const Block = styled.div`display: flow-root; min-width: 0; overflow-wrap: anywhere;`;
const PageFrame = styled.div`position: relative; margin: 0 auto 18px;`;
const Paper = styled.div`position: absolute; top: 0; left: 0; transform-origin: top left; box-sizing: border-box; padding: 48px; background: white; color: #171717; box-shadow: 0 2px 12px #0002;`;
const PageNumber = styled.div`position: absolute; bottom: 14px; left: 0; right: 0; text-align: center; font: 11px system-ui; color: #737a82;`;
const Empty = styled.div`padding: 50px 20px; text-align: center;`;
